import { gql } from '@apollo/client'
import { apolloClient } from '../apollo/client'
import type { Atleta } from './ligaService'
import type { Torneio } from './torneioService'

export type { Atleta } from './ligaService'

export interface Seguidor {
  id: string
  seguidor?: Atleta | null
  seguidoAtleta?: Atleta | null
  seguidoTorneio?: Torneio | null
  seguidoLigaId?: string | null
  dataFollow?: string | null
}

export const CRIAR_SEGUIDOR_MUTATION = gql`
  mutation CriarSeguidor($input: SeguidorInput!) {
    criarSeguidor(input: $input) {
      id
      dataFollow
      seguidoAtleta { id }
      seguidoTorneio { id }
      seguidoLigaId
      seguidor { id }
    }
  }
`

export interface CriarSeguidorInput {
  seguidorId: string
  seguidoAtletaId?: string | null
  seguidoTorneioId?: string | null
  seguidoLigaId?: string | null
}

export interface CriarSeguidorVars {
  input: CriarSeguidorInput
}

export interface CriarSeguidorResponse {
  id: string
  dataFollow: string
  seguidoAtleta?: { id: string } | null
  seguidoTorneio?: { id: string } | null
  seguidoLigaId?: string | null
  seguidor: { id: string }
}

export const SEGUIDOR_USUARIO_TORNEIO = gql`
  query SeguidorUsuarioTorneio($seguidorId: ID!, $seguidoTorneioId: ID!) {
    seguidorPorSeguidorIdSeguidoTorneioId(
      seguidorId: $seguidorId
      seguidoTorneioId: $seguidoTorneioId
    ) {
      id
    }
  }
`

export interface SeguidorUsuarioTorneioVars {
  seguidorId: string
  seguidoTorneioId: string
}

export interface SeguidorUsuarioTorneioData {
  seguidorPorSeguidorIdSeguidoTorneioId?: { id: string } | null
}

export const buscarSeguidorTorneio = async (
  seguidorId: string,
  seguidoTorneioId: string
) => {
  const { data } = await apolloClient.query<
    SeguidorUsuarioTorneioData,
    SeguidorUsuarioTorneioVars
  >({
    query: SEGUIDOR_USUARIO_TORNEIO,
    variables: { seguidorId, seguidoTorneioId },
    fetchPolicy: 'network-only'
  })
  return data.seguidorPorSeguidorIdSeguidoTorneioId
}

export const SEGUIDOR_USUARIO_ATLETA = gql`
  query SeguidorUsuarioAtleta($seguidorId: ID!, $seguidoAtletaId: ID!) {
    seguidorPorSeguidorIdSeguidoAtletaId(
      seguidorId: $seguidorId
      seguidoAtletaId: $seguidoAtletaId
    ) {
      id
    }
  }
`

export interface SeguidorUsuarioAtletaVars {
  seguidorId: string
  seguidoAtletaId: string
}

export interface SeguidorUsuarioAtletaData {
  seguidorPorSeguidorIdSeguidoAtletaId?: { id: string } | null
}

export const buscarSeguidorAtleta = async (
  seguidorId: string,
  seguidoAtletaId: string
) => {
  const { data } = await apolloClient.query<
    SeguidorUsuarioAtletaData,
    SeguidorUsuarioAtletaVars
  >({
    query: SEGUIDOR_USUARIO_ATLETA,
    variables: { seguidorId, seguidoAtletaId },
    fetchPolicy: 'network-only'
  })
  return data.seguidorPorSeguidorIdSeguidoAtletaId
}

export const DELETAR_SEGUIDOR_MUTATION = gql`
  mutation DeletarSeguidor($id: ID!) {
    deletarSeguidor(id: $id)
  }
`

export interface DeletarSeguidorVars {
  id: string
}

export interface DeletarSeguidorData {
  deletarSeguidor: boolean
}

export const SEGUIDOR_USUARIO_LIGA = gql`
  query SeguidorUsuarioLiga($seguidorId: ID!, $seguidoLigaId: ID!) {
    seguidorPorSeguidorIdSeguidoLigaId(
      seguidorId: $seguidorId
      seguidoLigaId: $seguidoLigaId
    ) {
      id
    }
  }
`

export interface SeguidorUsuarioLigaVars {
  seguidorId: string
  seguidoLigaId: string
}

export interface SeguidorUsuarioLigaData {
  seguidorPorSeguidorIdSeguidoLigaId?: { id: string } | null
}

export const buscarSeguidorLiga = async (
  seguidorId: string,
  seguidoLigaId: string
) => {
  const { data } = await apolloClient.query<
    SeguidorUsuarioLigaData,
    SeguidorUsuarioLigaVars
  >({
    query: SEGUIDOR_USUARIO_LIGA,
    variables: { seguidorId, seguidoLigaId },
    fetchPolicy: 'network-only'
  })
  return data.seguidorPorSeguidorIdSeguidoLigaId
}

export const SEGUIDORES_POR_TORNEIO = gql`
  query SeguidoresPorTorneio($torneioId: ID!) {
    seguidoresPorTorneioId(torneioId: $torneioId) {
      id
      dataFollow
      seguidor {
        id
        usuario {
          id
          nome
          email
          imagemUrl
          username
        }
      }
    }
  }
`

export interface SeguidoresPorTorneioData {
  seguidoresPorTorneioId: Seguidor[]
}

export interface SeguidoresPorTorneioVars {
  torneioId: string
}

// --------------------------------------------------------------------------------
// Query paginada: quem o atleta segue (atletas, torneios, ligas)
// --------------------------------------------------------------------------------

export interface SeguidoItem {
  id: string
  dataFollow?: string | null
  seguidoAtleta?: {
    id: string
    usuario: {
      id: string
      nome: string
      imagemUrl?: string | null
      username: string
    }
  } | null
  seguidoTorneio?: {
    id: string
    nome: string
    avatarUrl?: string | null
    username?: string | null
  } | null
  seguidoLiga?: {
    id: string
    nome: string
    username: string
    imagemUrl?: string | null
  } | null
  seguidoLigaId?: string | null
}

export const SEGUIDOS_POR_ATLETA = gql`
  query SeguidosPorAtletaId($atletaId: ID!, $page: Int!, $size: Int!) {
    seguidosPorAtletaId(
      atletaId: $atletaId
      page: $page
      size: $size
      sortBy: "dataFollow"
      direction: "DESC"
    ) {
      page
      size
      totalItems
      totalPages
      items {
        id
        dataFollow
        seguidoAtleta {
          id
          usuario {
            id
            nome
            imagemUrl
            username
          }
        }
        seguidoTorneio {
          id
          nome
          avatarUrl
          username
        }
        seguidoLiga {
          id
          nome
          username
          imagemUrl
        }
        seguidoLigaId
      }
    }
  }
`

export interface SeguidosPorAtletaData {
  seguidosPorAtletaId: {
    page: number
    size: number
    totalItems: number
    totalPages: number
    items: SeguidoItem[]
  }
}

export interface SeguidosPorAtletaVars {
  atletaId: string
  page: number
  size: number
}

export const listarSeguidosPorAtleta = async (
  atletaId: string,
  page: number,
  size: number
) => {
  const { data } = await apolloClient.query<
    SeguidosPorAtletaData,
    SeguidosPorAtletaVars
  >({
    query: SEGUIDOS_POR_ATLETA,
    variables: { atletaId, page, size },
    fetchPolicy: 'no-cache',
  })
  return data.seguidosPorAtletaId
}
